import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { apiUrl } from "../auth/config";
import { useAuth } from "../auth/AuthContext";
import "../styles/app.css";
import "../styles/proofs.css";

export default function ProofDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, loading, isAuthenticated, isAdmin, logout, getIdToken } =
    useAuth();

  const [post, setPost] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [fetching, setFetching] = useState(true);
  const [busy, setBusy] = useState(false);

  const authedFetch = useCallback(
    async (path, options = {}) => {
      const token = await getIdToken();
      return fetch(`${apiUrl}${path}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          ...(options.headers || {}),
        },
      });
    },
    [getIdToken]
  );

  const load = useCallback(async () => {
    if (!postId) return;
    setFetching(true);
    setError("");
    try {
      const res = await authedFetch(`/proofs/${encodeURIComponent(postId)}`);
      if (res.status === 403) {
        setError("Admin role required.");
        return;
      }
      if (!res.ok) throw new Error(`Could not load post (${res.status})`);
      const data = await res.json();
      setPost(data.post);
      setNote(data.post?.reviewNote || "");
    } catch (err) {
      setError(err.message || "Load failed");
      setPost(null);
    } finally {
      setFetching(false);
    }
  }, [authedFetch, postId]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    load();
  }, [isAuthenticated, isAdmin, load]);

  function startEdit() {
    setDraft({
      title: post.title || "",
      slug: post.slug || "",
      author: post.author || "",
      category: post.category || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      date: post.date || "",
      tags: (post.tags || []).join(", "),
    });
    setEditing(true);
    setError("");
    setInfo("");
  }

  async function saveEdit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const res = await authedFetch(`/proofs/${encodeURIComponent(postId)}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: draft.title,
          slug: draft.slug,
          author: draft.author,
          category: draft.category,
          excerpt: draft.excerpt,
          content: draft.content,
          date: draft.date || null,
          tags: draft.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
      setPost(data.post);
      setEditing(false);
      setInfo("Saved. Edits are on the proof and will go out when published.");
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function review(action) {
    if (action !== "approve") {
      if (note.trim().length < 8) {
        setError(
          "Add a note (≥8 characters) explaining why — it trains the next SEO drafts."
        );
        return;
      }
      const ok = window.confirm(
        `${action === "decline" ? "Decline" : "Sideline"} this proof?`
      );
      if (!ok) return;
    } else if (
      !window.confirm(
        "Approve this post? It enters the daily publish queue (≤1 per day)."
      )
    ) {
      return;
    }

    setBusy(true);
    setError("");
    setInfo("");
    try {
      const res = await authedFetch(
        `/proofs/${encodeURIComponent(postId)}/review`,
        {
          method: "POST",
          body: JSON.stringify({ action, note: note.trim() }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Review failed (${res.status})`);
      setPost(data.post);
      const buf = data.buffer;
      const qs = buf?.queues;
      const bufMsg = qs
        ? ` Queues: ${["real-estate", "injury", "estate"]
            .map((id) => {
              const q = qs[id];
              return q ? `${q.queueLabel} ${q.proof}/${q.target}` : null;
            })
            .filter(Boolean)
            .join(" · ")}.`
        : buf
          ? ` Buffer ${buf.proof} proofs · ${buf.queued} queued.`
          : "";
      setInfo(`Marked ${data.post.status}.${bufMsg}`);
    } catch (err) {
      setError(err.message || "Review failed");
    } finally {
      setBusy(false);
    }
  }

  async function deletePost() {
    const publishedNote =
      post?.status === "published"
        ? " The public blog export updates immediately — the live site should drop it within about a minute (no redeploy)."
        : "";
    if (
      !window.confirm(
        `Permanently delete “${post?.title || "this post"}”?${publishedNote}`
      )
    ) {
      return;
    }

    setBusy(true);
    setError("");
    setInfo("");
    try {
      const res = await authedFetch(`/proofs/${encodeURIComponent(postId)}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Delete failed (${res.status})`);
      navigate("/proofs", {
        replace: true,
        state: {
          flash: data.wasPublished
            ? "Deleted. Published export refreshed — live blog will drop it shortly (no redeploy)."
            : "Deleted.",
        },
      });
    } catch (err) {
      setError(err.message || "Delete failed");
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <p className="leads-support">Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/signin" replace />;
  if (!isAdmin) return <Navigate to="/proofs" replace />;

  const canReview =
    post &&
    ["proof", "draft", "declined", "sidelined", "approved"].includes(post.status) &&
    post.status !== "published";

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link className="app-header__brand" to="/">
          FTOJ
        </Link>
        <div className="app-header__meta">
          <Link to="/leads">Inbox</Link>
          <Link to="/proofs">Proofs</Link>
          <Link to="/security">Security</Link>
          <span>{user?.signInDetails?.loginId || user?.username}</span>
          <button className="btn btn--ghost" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main app-main--wide">
        <p className="detail-back">
          <Link to="/proofs">← Proofs</Link>
        </p>

        {fetching ? <p className="leads-support">Loading…</p> : null}
        {error ? <p className="auth-error">{error}</p> : null}
        {info ? <p className="auth-info">{info}</p> : null}

        {post && !editing ? (
          <>
            <div className="leads-heading">
              <div>
                <p className="proof-kicker">
                  <span className={`proof-status proof-status--${post.status}`}>
                    {post.status}
                  </span>
                  <span>{post.category}</span>
                </p>
                <h1 className="leads-title">{post.title}</h1>
                <p className="leads-support">
                  {post.author}
                  {post.date ? ` · ${post.date}` : ""} · /{post.slug}
                </p>
              </div>
              <div className="leads-heading-actions">
                {post.status !== "published" ? (
                  <button className="btn btn--primary" type="button" onClick={startEdit}>
                    Edit content
                  </button>
                ) : null}
                <button className="btn btn--ghost" type="button" onClick={load}>
                  Refresh
                </button>
                <button
                  className="btn btn--danger"
                  type="button"
                  disabled={busy}
                  onClick={deletePost}
                >
                  Delete
                </button>
              </div>
            </div>

            <article className="proof-article">
              <p className="proof-excerpt">{post.excerpt}</p>
              <div className="proof-body">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </div>
              {post.tags?.length ? (
                <p className="proof-tags">{post.tags.join(" · ")}</p>
              ) : null}
            </article>

            {canReview ? (
              <section className="proof-actions leads-panel">
                <h2>Review</h2>
                <label>
                  Note (required for sideline / decline — trains SEO skill)
                  <textarea
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Too plain — need O.C.G.A. cite; wrong practice angle; tone too salesy…"
                  />
                </label>
                <div className="proof-action-row">
                  <button
                    className="btn btn--primary"
                    type="button"
                    disabled={busy}
                    onClick={() => review("approve")}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn--ghost"
                    type="button"
                    disabled={busy}
                    onClick={() => review("sideline")}
                  >
                    Sideline
                  </button>
                  <button
                    className="btn btn--ghost"
                    type="button"
                    disabled={busy}
                    onClick={() => review("decline")}
                  >
                    Decline
                  </button>
                </div>
                <p className="detail-muted">
                  <strong>Approve</strong> only queues the post — the daily job
                  still publishes <strong>at most one</strong> approved article
                  per day into the public export.{" "}
                  <strong>Delete</strong> removes a published post from that
                  export immediately (live site updates on the next page load;
                  no redeploy).
                </p>
              </section>
            ) : null}

            {post.reviewedAt ? (
              <p className="detail-muted">
                Last review: {post.reviewedByEmail} · {post.reviewedAt}
                {post.reviewNote ? ` — ${post.reviewNote}` : ""}
              </p>
            ) : null}
          </>
        ) : null}

        {post && editing && draft ? (
          <form className="proof-edit" onSubmit={saveEdit}>
            <h1 className="leads-title">Edit proof</h1>
            <label>
              Title
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                required
              />
            </label>
            <label>
              Slug
              <input
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                required
              />
            </label>
            <div className="proof-edit-grid">
              <label>
                Author
                <input
                  value={draft.author}
                  onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                />
              </label>
              <label>
                Category
                <input
                  value={draft.category}
                  onChange={(e) =>
                    setDraft({ ...draft, category: e.target.value })
                  }
                />
              </label>
              <label>
                Display date
                <input
                  value={draft.date}
                  onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                  placeholder="May 30, 2025"
                />
              </label>
            </div>
            <label>
              Excerpt
              <textarea
                rows={2}
                value={draft.excerpt}
                onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
              />
            </label>
            <label>
              Tags (comma-separated)
              <input
                value={draft.tags}
                onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
              />
            </label>
            <label>
              Body (markdown)
              <textarea
                rows={18}
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                required
              />
            </label>
            <div className="proof-action-row">
              <button className="btn btn--primary" type="submit" disabled={busy}>
                {busy ? "Saving…" : "Save edits"}
              </button>
              <button
                className="btn btn--ghost"
                type="button"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setDraft(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {!fetching && !post && !error ? (
          <section className="leads-panel">
            <h2>Not found</h2>
            <button className="btn btn--ghost" type="button" onClick={() => navigate("/proofs")}>
              Back
            </button>
          </section>
        ) : null}
      </main>
    </div>
  );
}
